import { useCallback, useEffect, useRef, useState } from 'react';
import { listResidents } from '../services/Resident';
import { normalizeId } from '../../utils/normalizeId';

export interface ResidentIdentity {
    FirstName: string;
    LastName: string;
    Name: string;
    Image: string;
}

export type ResidentIdentityMap = Record<string, ResidentIdentity>;

// Posts and feed items denormalize the author's name/image at write time from the
// resident's *speak2* record, so they never reflect the resident's Neighbors social
// profile. listResidents runs with Directory: true, which is the one path where the
// backend already resolves the social-profile priority (SocialProfile.FirstName /
// LastName / Image ahead of the official record), so we join by id and read the
// resolved values off the response rather than reimplementing that priority here.
// listResidents is called with an explicit id filter, so keep batches bounded —
// an unbounded id list here turns a display enrichment call into a large POST.
const MAX_BATCH_SIZE = 50;

const useResidentIdentities = (facilityId?: string, residentIds?: string[]) => {
    const [identityById, setIdentityById] = useState<ResidentIdentityMap>({});
    // Ids we've successfully resolved a response for (found or confirmed absent),
    // so paging through posts only ever requests authors we haven't seen yet.
    const requestedIdsRef = useRef<Set<string>>(new Set());
    // Ids with a fetch currently in flight — kept separate from requestedIdsRef so
    // a failed fetch doesn't permanently blacklist its ids from ever being retried,
    // while still preventing duplicate concurrent requests for the same id.
    const pendingIdsRef = useRef<Set<string>>(new Set());
    // Switching tabs unmounts the feed mid-flight, so the late response must not
    // try to set state on a component that is gone.
    const isMountedRef = useRef(true);
    // Always mirrors the latest facilityId (updated during render, no effect
    // needed), so an in-flight response can tell whether it's still for the
    // facility currently being viewed before merging into shared state.
    const facilityIdRef = useRef(facilityId);
    facilityIdRef.current = facilityId;

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // Resets on facility change so one community's identities can't leak into another.
    useEffect(() => {
        requestedIdsRef.current = new Set();
        pendingIdsRef.current = new Set();
        setIdentityById({});
    }, [facilityId]);

    const wantedIds = (residentIds || []).map(normalizeId).filter(Boolean);
    const wantedKey = Array.from(new Set(wantedIds)).sort().join(',');

    const fetchIdentities = useCallback(async (ids: string[]) => {
        if (!facilityId || !ids.length) return;
        // Captured once per call, so a response can be checked against whatever
        // facilityId is current by the time it lands, not just at call time.
        const requestFacilityId = facilityId;
        const batches: string[][] = [];
        for (let i = 0; i < ids.length; i += MAX_BATCH_SIZE) {
            batches.push(ids.slice(i, i + MAX_BATCH_SIZE));
        }
        await Promise.all(batches.map(async (batch) => {
            try {
                const response = await listResidents({
                    facilityId,
                    residentIds: batch,
                    page_no: 1,
                    page_size: batch.length,
                });
                const resolved: ResidentIdentityMap = {};
                (response.Residents || []).forEach(resident => {
                    const residentId = normalizeId(resident._id);
                    if (!residentId) return;
                    const firstName = resident.FirstName || '';
                    const lastName = resident.LastName || '';
                    const name = `${firstName} ${lastName}`.trim();
                    if (!name && !resident.Image) return;
                    resolved[residentId] = {
                        FirstName: firstName,
                        LastName: lastName,
                        Name: name,
                        Image: resident.Image || '',
                    };
                });
                // A facility switch mid-flight already reset requestedIdsRef/identityById
                // for the new facility; without this check, a late response for the old
                // facility would still merge in and leak that facility's data into it.
                if (isMountedRef.current && facilityIdRef.current === requestFacilityId) {
                    // The response is authoritative for every id in this batch, found
                    // or not, so mark them all requested — only a thrown error (network
                    // failure, timeout) should leave ids eligible for retry.
                    batch.forEach(id => requestedIdsRef.current.add(id));
                    if (Object.keys(resolved).length) {
                        setIdentityById(prev => ({ ...prev, ...resolved }));
                    }
                }
            } catch {
                // Display-only enrichment; the caller still renders the denormalized
                // name/image it already has, so a failure here is not worth a toast.
                // Deliberately not added to requestedIdsRef — a later render with the
                // same id (e.g. more posts loaded) will retry it.
            } finally {
                batch.forEach(id => pendingIdsRef.current.delete(id));
            }
        }));
    }, [facilityId]);

    useEffect(() => {
        const unseen = Array.from(new Set(wantedIds)).filter(
            id => !requestedIdsRef.current.has(id) && !pendingIdsRef.current.has(id)
        );
        if (!unseen.length) return;
        unseen.forEach(id => pendingIdsRef.current.add(id));
        fetchIdentities(unseen);
        // wantedKey collapses the id list to a stable primitive; wantedIds itself is a
        // fresh array every render and would re-fire this effect endlessly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wantedKey, fetchIdentities]);

    return identityById;
};

export default useResidentIdentities;
